import type { TFunction } from 'i18next';
import type {
  ActionPlan,
  ActionPlanning,
  AreaDecisionType,
  PlanArea,
  PlanError,
  PlanRequest,
  Proposal,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import type { FieldError } from './assessmentView';
import { joinList } from '../HRDashboard/hmsFormat';
import { longDate } from '../Settings/privacyView';
import { areaName, dayAndTime } from './riskView';
import { missingSuffix } from './safetyRepView';

/*
 * The rules of the plan are checked in the API, which is the source of truth. What is here
 * only shows the right fields while the user fills in the form.
 */

export const completesPlanStep = (status: WorkEnvironmentSurveyStatus) =>
  status === WorkEnvironmentSurveyStatus.ACTION_PLAN_PENDING;

export const showsPlanPage = (survey: Pick<WorkEnvironmentSurvey, 'status' | 'riskCalculatedAt'>) =>
  Boolean(survey.riskCalculatedAt) && completesPlanStep(survey.status);

/** Putting off high risk or accepting moderate risk needs a reason */
export const decisionNeedsReason = (decision: AreaDecisionType | null) =>
  decision === 'NOT_NOW' || decision === 'ACCEPT';

/** The plan can only be sent for approval when the team has a safety representative */
export type ReviewMissing = ActionPlanning['reviewMissing'];

export const hasSafetyRep = (planning: Pick<ActionPlanning, 'reviewMissing'>) => planning.reviewMissing === null;

/** Where the organisation does not use safety representatives, the team leader approves the plan alone */
export const approvesAlone = (planning: Pick<ActionPlanning, 'reviewMissing'>) => planning.reviewMissing === 'notUsed';

/** Why approving alone is a deviation, and an example of a reason, for why the safety representative is missing */
export const withoutApprovalTexts = (reviewMissing: ReviewMissing, t: TFunction) => {
  const suffix = missingSuffix(reviewMissing);
  return {
    text: t(`workEnvironmentPlan.builder.without.text${suffix}`),
    reasonHelp: t(`workEnvironmentPlan.builder.without.reasonHelp${suffix}`),
  };
};

export type PlanStatus = 'none' | ActionPlan['approvalState'];

export const planStatus = (area: Pick<PlanArea, 'plan'>): PlanStatus => area.plan?.approvalState ?? 'none';

export const PLAN_STATUS_COLORS: Record<Exclude<PlanStatus, 'none'>, 'neutral' | 'info' | 'success'> = {
  draft: 'neutral',
  awaitingApproval: 'info',
  approved: 'success',
};

/**
 * The team leader makes the plans. The safety representative approves them. The role from the
 * plan stays a guard for a backend that does not yet say what the viewer may do.
 */
export const makesPlans = (planning: Pick<ActionPlanning, 'canChange' | 'role'>, mayMakePlan: boolean) =>
  planning.canChange && mayMakePlan && planning.role !== 'SAFETY_REP';

/** Areas at risk first. Low risk is there too, but out of the way. */
export const areasByRisk = (areas: PlanArea[]) => ({
  atRisk: areas.filter((area) => area.level !== 'low'),
  low: areas.filter((area) => area.level === 'low'),
});

/** One plan is enough. Once the team has one, the other areas are better served by a decision. */
export const hasPlan = (areas: PlanArea[]) => areas.some((area) => planStatus(area) !== 'none');

/** Areas at risk are dealt with by an approved plan or a decision */
export const isDealtWith = (area: PlanArea) => planStatus(area) === 'approved' || Boolean(area.decision);

export const decisionProgress = (areas: PlanArea[]) => {
  const { atRisk } = areasByRisk(areas);
  return { done: atRisk.filter(isDealtWith).length, total: atRisk.length };
};

/** The team leader edits the plan. The safety representative approves it, and never edits it. */
export const editsPlan = (
  planning: Pick<ActionPlanning, 'canChange' | 'role'>,
  area: Pick<PlanArea, 'plan'>,
  editing: boolean,
  mayMakePlan: boolean,
) =>
  makesPlans(planning, mayMakePlan) &&
  (!area.plan || area.plan.approvalState === 'draft' || (area.plan.approvalState === 'approved' && editing));

export type ApprovalNotice = 'askedToApprove' | 'awaitingApproval' | 'approvedWithoutSafetyRep' | 'approved';

/** What the page says about a plan that is not being edited */
export const approvalNotice = (
  plan: Pick<ActionPlan, 'approvalState' | 'approved'>,
  role: ActionPlanning['role'],
): ApprovalNotice | null => {
  if (plan.approvalState === 'awaitingApproval') return role === 'SAFETY_REP' ? 'askedToApprove' : 'awaitingApproval';
  if (plan.approvalState === 'draft') return null;
  return plan.approved?.deviation ? 'approvedWithoutSafetyRep' : 'approved';
};

/** «Dere følger opp månedlig, og planen evalueres 28. mars 2027.» */
export const followUpText = (
  plan: Pick<ActionPlan, 'frequency' | 'evaluationDate'>,
  t: TFunction,
  language: string,
) =>
  plan.frequency && plan.evaluationDate
    ? t('workEnvironmentPlan.approval.followUp', {
        frequency: t(`workEnvironmentPlan.builder.frequencies.${plan.frequency}`).toLowerCase(),
        date: longDate(plan.evaluationDate, language),
      })
    : null;

export const isApproved = (notice: ApprovalNotice) => notice === 'approved' || notice === 'approvedWithoutSafetyRep';

/** What the notice says, with how the plan is followed up once it is approved */
export const noticeText = (
  notice: ApprovalNotice,
  plan: Pick<ActionPlan, 'frequency' | 'evaluationDate'>,
  reviewMissing: ReviewMissing,
  t: TFunction,
  language: string,
) =>
  [
    t(
      notice === 'approved' && reviewMissing === 'notUsed'
        ? 'workEnvironmentPlan.approval.approved.textNotUsed'
        : notice === 'approvedWithoutSafetyRep' && reviewMissing
        ? 'workEnvironmentPlan.approval.approvedWithoutSafetyRep.textNoRep'
        : `workEnvironmentPlan.approval.${notice}.text`,
    ),
    isApproved(notice) && followUpText(plan, t, language),
  ]
    .filter(Boolean)
    .join(' ');

/** Where the safety representative is with the plan. An approved plan says so in its notice. */
export const reviewStatusText = (
  plan: Pick<ActionPlan, 'approvalState' | 'reviewRequestedAt'> | null,
  reviewMissing: ReviewMissing,
  t: TFunction,
  language: string,
) => {
  if (reviewMissing === 'notUsed') return null;
  if (reviewMissing) return t(`workEnvironmentPlan.review.${reviewMissing}`);
  if (plan?.approvalState === 'approved') return null;
  return plan?.reviewRequestedAt
    ? t('workEnvironmentPlan.review.requested', { when: dayAndTime(plan.reviewRequestedAt, t, language) })
    : t('workEnvironmentPlan.review.notRequested');
};

const clockTime = (value: Date, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', { hour: '2-digit', minute: '2-digit' }).format(value);

/** «2 tiltak i planen · oppfølging månedlig», or when the draft was last saved */
export const builderStatus = (
  form: Pick<PlanRequest, 'measures' | 'frequency'>,
  savedAt: Date | null,
  t: TFunction,
  language: string,
) => {
  const count = form.measures.length;
  if (savedAt) return t('workEnvironmentPlan.builder.savedAt', { time: clockTime(savedAt, language), count });
  if (count === 0) return t('workEnvironmentPlan.builder.noMeasures');
  return form.frequency
    ? t('workEnvironmentPlan.builder.countWithFollowUp', {
        count,
        frequency: t(`workEnvironmentPlan.builder.frequencies.${form.frequency}`).toLowerCase(),
      })
    : t('workEnvironmentPlan.builder.count', { count });
};

export type PlanForm = PlanRequest;

type FormMeasure = PlanForm['measures'][number];

/** A new plan starts with the areas the team leader chose */
export const planForm = (plan: ActionPlan | null, areaKeys: string[]): PlanForm => ({
  areas: plan
    ? plan.areas.map(({ factorKey, goal }) => ({ factorKey, goal: goal ?? '' }))
    : areaKeys.map((factorKey) => ({ factorKey, goal: '' })),
  frequency: plan?.frequency ?? null,
  measureSafety: plan?.measureSafety ?? false,
  measurePulse: plan?.measurePulse ?? false,
  evaluationDate: plan?.evaluationDate ?? null,
  measures: (plan?.measures ?? []).map(({ id, title, description, ownerId, dueDate, proposalIds, factorKeys }) => ({
    id,
    title,
    description: description ?? '',
    ownerId,
    dueDate,
    proposalIds,
    factorKeys,
  })),
});

export const formAreaKeys = (form: Pick<PlanForm, 'areas'>) => form.areas.map((area) => area.factorKey);

/** With one area, every measure helps with it, so the form does not ask */
export const asksForAreas = (form: Pick<PlanForm, 'areas'>) => form.areas.length > 1;

export const emptyMeasure = (dueDate: string, form: Pick<PlanForm, 'areas'>): FormMeasure => ({
  title: '',
  description: '',
  ownerId: null,
  dueDate,
  proposalIds: [],
  factorKeys: asksForAreas(form) ? [] : formAreaKeys(form),
});

/** A proposal from the team becomes a measure with its words, for the area it was proposed for */
export const measureFromProposal = (
  proposal: Pick<Proposal, 'id' | 'title' | 'reason' | 'factorKey'>,
  dueDate: string,
  form: Pick<PlanForm, 'areas'>,
): FormMeasure => ({
  ...emptyMeasure(dueDate, form),
  title: proposal.title,
  description: proposal.reason ?? '',
  proposalIds: [proposal.id],
  factorKeys: [proposal.factorKey],
});

export const toggleMeasureArea = (measure: FormMeasure, factorKey: string): FormMeasure => ({
  ...measure,
  factorKeys: measure.factorKeys.includes(factorKey)
    ? measure.factorKeys.filter((key) => key !== factorKey)
    : [...measure.factorKeys, factorKey],
});

export const withGoal = (form: PlanForm, factorKey: string, goal: string): PlanForm => ({
  ...form,
  areas: form.areas.map((area) => (area.factorKey === factorKey ? { ...area, goal } : area)),
});

/** How many measures help with each area. Every area needs at least one. */
export const coverage = (form: Pick<PlanForm, 'areas' | 'measures'>) =>
  form.areas.map((area) => ({
    factorKey: area.factorKey,
    count: form.measures.filter((measure) => measure.factorKeys.includes(area.factorKey)).length,
  }));

/** The team's proposals for the areas of the plan */
export const proposalsFor = (proposals: Proposal[], form: Pick<PlanForm, 'areas'>) =>
  proposals.filter((proposal) => formAreaKeys(form).includes(proposal.factorKey));

export const PLAN_FIELDS = {
  areas: 'plan-areas',
  measures: 'plan-add-measure',
  goal: (factorKey: string) => `plan-goal-${factorKey}`,
  areaMeasures: () => 'plan-coverage',
  frequency: 'plan-frequency-WEEKLY',
  evaluationDate: 'plan-evaluation',
  deviation: 'plan-deviation',
  measureTitle: (index: number) => `plan-measure-${index}-title`,
  measureOwner: (index: number) => `plan-measure-${index}-owner`,
  measureDue: (index: number) => `plan-measure-${index}-due`,
  measureProposals: (index: number) => `plan-measure-${index}-title`,
  measureAreas: (index: number) => `plan-measure-${index}-area-0`,
};

export const planFieldErrors = (
  errors: PlanError[],
  t: TFunction,
  nameOf: (factorKey: string) => string,
): FieldError[] =>
  errors.map((error) => {
    if ('index' in error)
      return {
        fieldId: PLAN_FIELDS[error.type](error.index),
        message: t(`workEnvironmentPlan.errors.${error.type}`, { number: error.index + 1 }),
      };
    if ('factorKey' in error)
      return {
        fieldId: PLAN_FIELDS[error.type](error.factorKey),
        message: t(`workEnvironmentPlan.errors.${error.type}`, { name: nameOf(error.factorKey).toLowerCase() }),
      };
    return { fieldId: PLAN_FIELDS[error.type], message: t(`workEnvironmentPlan.errors.${error.type}`) };
  });

/** Each plan once, with the areas it deals with, in the order of the areas */
export const plansOf = (areas: PlanArea[]) =>
  areas.reduce<Array<{ plan: ActionPlan; areas: PlanArea[] }>>((plans, area) => {
    if (!area.plan) return plans;
    const found = plans.find((entry) => entry.plan.id === area.plan?.id);
    return found
      ? plans.map((entry) => (entry === found ? { ...entry, areas: [...entry.areas, area] } : entry))
      : [...plans, { plan: area.plan, areas: [area] }];
  }, []);

/** The areas a plan deals with */
export const planAreas = (plan: Pick<ActionPlan, 'areas'>, areas: PlanArea[]) =>
  plan.areas.flatMap((planArea) => areas.filter((area) => area.key === planArea.factorKey));

/** «kontroll og innflytelse» or «kontroll og innflytelse og emosjonelle krav» */
export const areaNames = (areas: Array<Pick<PlanArea, 'kind' | 'name'>>, t: TFunction) =>
  joinList(
    areas.map((area) => areaName(area, t).toLowerCase()),
    t,
    'and',
  );

/** «Tiltaksplan for kontroll og innflytelse», or for how many areas */
export const planHeading = (areas: Array<Pick<PlanArea, 'kind' | 'name'>>, t: TFunction) =>
  areas.length === 1
    ? t('workEnvironmentPlan.builder.heading', { name: areaName(areas[0], t).toLowerCase() })
    : t('workEnvironmentPlan.builder.headingMany', { count: areas.length });

/** What the page has open: a plan, the plan of an area, or the areas of a new plan */
export type OpenPlan = { kind: 'plan'; id: number } | { kind: 'area'; key: string } | { kind: 'new'; keys: string[] };

export const openedPlan = (areas: PlanArea[], open: OpenPlan | null) => {
  if (!open) return null;
  if (open.kind === 'new') return { plan: null, areas: areas.filter((area) => open.keys.includes(area.key)) };
  const plan =
    open.kind === 'plan'
      ? (plansOf(areas).find((entry) => entry.plan.id === open.id)?.plan ?? null)
      : (areas.find((area) => area.key === open.key)?.plan ?? null);
  return plan ? { plan, areas: planAreas(plan, areas) } : null;
};

/** The team leader continues a draft, and sees a plan that waits or is approved */
export const planAction = (plan: Pick<ActionPlan, 'approvalState'>, canChange: boolean) =>
  canChange && plan.approvalState === 'draft' ? 'continue' : 'view';

export const toggled = (keys: string[], key: string) =>
  keys.includes(key) ? keys.filter((candidate) => candidate !== key) : [...keys, key];

/** Areas at risk without a plan need a decision. Those still without one come first. */
export const areasToDecide = (areas: PlanArea[]) =>
  choosableAreas(areasByRisk(areas).atRisk).sort((a, b) => Number(Boolean(a.decision)) - Number(Boolean(b.decision)));

/** Areas at risk not yet in a plan can be chosen for a new one */
export const choosableAreas = (areas: PlanArea[]) => areas.filter((area) => !area.plan);

/** The measures of the plan that help with the area, as the follow-up shows them */
export const measuresFor = (plan: Pick<ActionPlan, 'measures'>, factorKey: string) =>
  plan.measures.filter((measure) => measure.factorKeys.includes(factorKey));

/** The plan as the follow-up of one area sees it, with only the measures for that area */
export const planForArea = (plan: ActionPlan, factorKey: string): ActionPlan => ({
  ...plan,
  measures: measuresFor(plan, factorKey),
});

export const goalFor = (plan: Pick<ActionPlan, 'areas'>, factorKey: string) =>
  plan.areas.find((area) => area.factorKey === factorKey)?.goal ?? null;

const isoDay = (date: Date) =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((part) => String(part).padStart(2, '0')).join('-');

const inMonths = (now: Date, months: number) => {
  const date = new Date(now);
  date.setMonth(date.getMonth() + months);
  return isoDay(date);
};

/** The evaluation is 6–12 months ahead, so the measures have time to work */
export const evaluationBounds = (now = new Date()) => ({ min: inMonths(now, 6), max: inMonths(now, 12) });

export const dayOf = (now = new Date()) => isoDay(now);

/** The area the plan step was opened at, from a link or the notice of what is left, or at the decision for it */
export type PlanFocus = { key: string; decision: boolean };

export const choiceId = (areaKey: string) => `plan-choice-${areaKey}`;

export const decisionId = (areaKey: string) => `decision-area-${areaKey}`;

export const focusId = (focus: PlanFocus) => (focus.decision ? decisionId(focus.key) : choiceId(focus.key));

/** An area with a plan opens it. Without one, the page shows the area among those to choose or decide. */
export const openFor = (focus: PlanFocus, areas: PlanArea[]): OpenPlan | null => {
  const withPlan = areas.some((area) => area.key === focus.key && area.plan);
  return !focus.decision && withPlan ? { kind: 'area', key: focus.key } : null;
};
